import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Loader2, Cloud, Check, X, Image as ImageIcon, Folder, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cloudStorageAPI } from '../../lib/api';

export const GoogleDrivePicker = ({ onFilesSelected, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(null); // null = loading, true/false = status
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [currentFolder, setCurrentFolder] = useState('root');
  const [folderStack, setFolderStack] = useState([{ id: 'root', name: 'My Drive' }]);
  const [nextPageToken, setNextPageToken] = useState(null);

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const response = await cloudStorageAPI.getStatus();
      setIsConnected(response.data.google_drive);
      if (response.data.google_drive) {
        // Auto-load files if connected
        loadFiles('root');
      }
    } catch (error) {
      console.error('Failed to check connection status:', error);
      setIsConnected(false);
    }
  };

  const loadFiles = async (folderId, pageToken = null, append = false) => {
    setIsLoading(true);
    try {
      const response = await cloudStorageAPI.listGoogleDriveFiles(folderId, pageToken);
      const newFiles = response.data.files || [];
      
      if (append) {
        setFiles(prev => [...prev, ...newFiles]);
      } else {
        setFiles(newFiles);
      }
      setNextPageToken(response.data.nextPageToken);
      setCurrentFolder(folderId);
    } catch (error) {
      console.error('Failed to load files:', error);
      if (error.response?.status === 400 || error.response?.status === 401) {
        // Connection expired or invalid
        setIsConnected(false);
        toast.error('Google Drive connection expired. Please reconnect from Settings.');
      } else {
        toast.error('Failed to load files from Google Drive');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToFolder = (folder) => {
    // If it's a folder (from the file list)
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
    setSelectedFiles([]); // Clear selection when navigating
    loadFiles(folder.id);
  };

  const navigateBack = () => {
    if (folderStack.length > 1) {
      const newStack = [...folderStack];
      newStack.pop();
      setFolderStack(newStack);
      const previousFolder = newStack[newStack.length - 1];
      setSelectedFiles([]); // Clear selection when navigating
      loadFiles(previousFolder.id);
    }
  };

  const navigateToBreadcrumb = (index) => {
    const newStack = folderStack.slice(0, index + 1);
    setFolderStack(newStack);
    const folder = newStack[newStack.length - 1];
    setSelectedFiles([]); // Clear selection when navigating
    loadFiles(folder.id);
  };

  const toggleFileSelection = (file) => {
    // Can't select folders
    if (file.mimeType === 'application/vnd.google-apps.folder') {
      navigateToFolder(file);
      return;
    }
    
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  const handleImport = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files first');
      return;
    }

    setIsImporting(true);
    
    try {
      // Transform files for import (no accessToken needed - backend uses stored credentials)
      const filesToImport = selectedFiles.map(file => ({
        id: file.id,
        fileId: file.id,
        name: file.name,
        mimeType: file.mimeType,
      }));
      
      await onFilesSelected(filesToImport, null); // accessToken is null - backend uses stored credentials
      toast.success(`${selectedFiles.length} image(s) imported successfully!`);
      setSelectedFiles([]);
      onClose?.();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import files: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsImporting(false);
    }
  };

  const handleConnectClick = () => {
    // Redirect to settings page for connection
    window.location.href = '/settings?tab=integrations';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  // Not connected state
  if (isConnected === false) {
    return (
      <div className="space-y-4">
        <div className="p-6 bg-yellow-50 rounded-xl border-2 border-yellow-200 text-center">
          <svg className="w-12 h-12 mx-auto mb-3" viewBox="0 0 24 24" fill="none">
            <path d="M4.5 14.5L8 9L15.5 9L12 14.5H4.5Z" fill="#4285F4"/>
            <path d="M8 9L12 2L19.5 2L15.5 9H8Z" fill="#0F9D58"/>
            <path d="M15.5 9L19.5 2L23 8.5L19 15.5L15.5 9Z" fill="#FFCD40"/>
          </svg>
          <h3 className="font-medium text-yellow-800 mb-2">Google Drive Not Connected</h3>
          <p className="text-sm text-yellow-700 mb-4">
            Connect your Google Drive account in Settings to import images.
          </p>
          <Button
            onClick={handleConnectClick}
            className="bg-[#4285F4] hover:bg-[#3367D6] text-white"
            data-testid="connect-google-drive-picker-btn"
          >
            <Cloud className="w-4 h-4 mr-2" />
            Connect Google Drive
          </Button>
        </div>
      </div>
    );
  }

  // Loading connection status
  if (isConnected === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4285F4]" />
      </div>
    );
  }

  // Connected - show file browser
  return (
    <div className="space-y-4">
      {/* Header with navigation */}
      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
            <path d="M4.5 14.5L8 9L15.5 9L12 14.5H4.5Z" fill="#4285F4"/>
            <path d="M8 9L12 2L19.5 2L15.5 9H8Z" fill="#0F9D58"/>
            <path d="M15.5 9L19.5 2L23 8.5L19 15.5L15.5 9Z" fill="#FFCD40"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-blue-900 truncate">Google Drive Connected</p>
          <p className="text-xs text-blue-700">Select images to import</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => loadFiles(currentFolder)}
          disabled={isLoading}
          className="text-blue-600"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Breadcrumb navigation */}
      <div className="flex items-center gap-1 text-sm overflow-x-auto py-1">
        {folderStack.map((folder, index) => (
          <div key={folder.id} className="flex items-center shrink-0">
            {index > 0 && <span className="text-gray-400 mx-1">/</span>}
            <button
              onClick={() => navigateToBreadcrumb(index)}
              className={`px-2 py-1 rounded hover:bg-gray-100 ${
                index === folderStack.length - 1 ? 'font-medium text-[#1E3A5F]' : 'text-gray-600'
              }`}
            >
              {folder.name}
            </button>
          </div>
        ))}
      </div>

      {/* Back button */}
      {folderStack.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={navigateBack}
          className="text-gray-600"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
      )}

      {/* File list */}
      <div className="border rounded-lg max-h-80 overflow-y-auto">
        {isLoading && files.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#4285F4]" />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Folder className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No images found in this folder</p>
          </div>
        ) : (
          <div className="divide-y">
            {files.map((file) => {
              const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
              const isSelected = selectedFiles.some(f => f.id === file.id);
              
              return (
                <div
                  key={file.id}
                  onClick={() => toggleFileSelection(file)}
                  className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`drive-file-${file.id}`}
                >
                  {/* Checkbox for images, folder icon for folders */}
                  <div className="w-5 h-5 flex items-center justify-center">
                    {isFolder ? (
                      <Folder className="w-5 h-5 text-[#4285F4]" />
                    ) : (
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-[#4285F4] border-[#4285F4]' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    )}
                  </div>
                  
                  {/* Thumbnail */}
                  <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                    {file.thumbnailLink ? (
                      <img 
                        src={file.thumbnailLink} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    ) : file.iconLink ? (
                      <img src={file.iconLink} alt="" className="w-6 h-6" />
                    ) : isFolder ? (
                      <Folder className="w-5 h-5 text-[#4285F4]" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  
                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {isFolder ? 'Folder' : formatFileSize(file.size)}
                    </p>
                  </div>
                  
                  {/* Navigate indicator for folders */}
                  {isFolder && (
                    <span className="text-xs text-gray-400">→</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Load more button */}
        {nextPageToken && (
          <div className="p-3 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadFiles(currentFolder, nextPageToken, true)}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Selected files count and import button */}
      {selectedFiles.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#1E3A5F]">
              {selectedFiles.length} file(s) selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFiles([])}
              className="text-red-500 hover:text-red-700"
            >
              Clear
            </Button>
          </div>
          
          <Button
            onClick={handleImport}
            disabled={isImporting}
            className="w-full bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
            data-testid="google-drive-import-btn"
          >
            {isImporting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
            ) : (
              <><Check className="w-4 h-4 mr-2" />Import {selectedFiles.length} Image(s)</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GoogleDrivePicker;
